import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Tenant role row returned by `GET /api/TenantRoles/GetRole/{typeId}?organizationId={orgId}`.
 *
 * Powers the wizard's ROLE dropdown when creating practitioners (typeId=3) or
 * staff (typeId=2). The numeric `tenantId` is the SAME enum as the typeId in
 * the path (3=practitioner, 2=staff) — distinct from the rich `permissiongrp`
 * payload below.
 *
 * Captured against dev 2026-05-21 (typeId=3 → "In-house Practitioner"
 * roleId=6, "Visiting Practitioner" roleId=18). Other fields like
 * `permissiongrp` are heavy and unused for a dropdown — included on the
 * type for parity but consumers should map to `{roleId, roleName}` only.
 */
export interface TenantRoleRow {
  roleId: number;
  tenantId: number;
  organizationId: string;
  roleName: string;
  roleDescription: string;
  isArchived: boolean;
  isNurse: boolean;
  permissiongrp?: unknown[];
}

/**
 * Tenant role types per `app_constants.dart` (Flutter `FetchRoleByTypeEvent(id: N)`):
 *   2 = staff roles (used by PR 5 Add Staff)
 *   3 = practitioner/doctor roles (used by PR 4 Add Practitioner)
 */
export type TenantRoleType = 2 | 3;

/**
 * Fetch tenant-role rows filtered by role type. Flutter parity:
 * `fetchRolesByType` in `settings_data_source_impl.dart:1151-1163` —
 *   GET /api/TenantRoles/GetRole/{id}?organizationId={orgId}
 *
 * Hook callers should filter `!isArchived` and project to
 * `{ value: roleId, label: roleName }` for dropdown rendering.
 */
export function useRolesByType(
  typeId: TenantRoleType,
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'roles-by-type', typeId, organizationId],
    queryFn: ({ signal }) => {
      if (!organizationId) {
        throw new Error('useRolesByType called without organizationId');
      }
      return get<TenantRoleRow[]>(
        `/api/TenantRoles/GetRole/${typeId}?organizationId=${organizationId}`,
        { signal },
      );
    },
    enabled: !!organizationId,
  });
}
